<template>
  <div class="nopads">
      <!-- Header of collapsable item -->
      <div class="col-12 d-flex justify-content-between align-items-center pb-1">
          <div class="flex-grow-1">
              <h4 class="form-header pointer hover-header" @click.stop="deliveriesVisible = !deliveriesVisible">
                  {{ $t('contract_reports.unload_records.title_notes') }} ({{ tableItems.length }})
              </h4>
          </div>
          <div
              v-if="reportsLoading"
              id="loader"
              class="spinner-section"
          />
          <div v-else class="ms-2 px-2" @click.stop="deliveriesVisible = !deliveriesVisible">
              <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated:deliveriesVisible }"/>
          </div>
      </div>
      <!-- Collapsable content -->
      <div class="routa-collapse" :class="{ collapsed: deliveriesVisible }">
          <div v-if="materials.station.length > 0" class="row">
            <div class="col-auto report-info">
              <div>{{ unloadedAmount.toFixed(1) }} / {{ totalAmount.toFixed(1) }} {{ unit }}</div>
            </div>
            <div class="col-auto report-info">
              <div>{{ Math.round(totalDistance) }} m</div>
            </div>
            <div class="col-auto report-info">
              <div>{{ Math.round(totalArea) }} m2</div>
            </div>
            <div class="col-auto report-info">
              <div>{{ averageDensity }} kg/m2</div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-4 dont-print">
              <div v-if="materials.station.length > 0" class="nopads">
                <span class="item-detail-title">{{ $t('delivery_note.material') }}</span>
                  <select
                      v-model="material"
                      class="form-select form-select-sm"
                  >
                      <option :value="null">
                          {{ $t('observations.search_all') }}
                      </option>
                      <option
                          v-for="material in materials.station"
                          :key="material.id"
                          :value="material.id"
                      >
                          {{ material.material.name + ' (' + material.type_test_code + ')' }}
                      </option>
                  </select>
              </div>
            </div>
            <div class="col-sm-8 d-flex justify-content-end dont-print pt-3">
              <button :disabled="tableItems.length < 1" class="btn btn-outline-success" @click="exportExcel">
                {{ $t('trip.download_excel') }}
              </button>
            </div>
          </div>

          <div v-if="tableItems.length > 0" class="list-content result-table mt-2 mb-4">
              <vue3-datatable
                  ref="resultTable"
                  :rows="tableItems"
                  :columns="fields"
                  :pagination="false"
                  :sticky-header="true"
                  height="55vh"
                  :sortable="true"
                  skin="bh-table-compact"
                  row-class="routa-table-row"
                  cell-class="routa-table-cell"
                  @row-click="showDeliveries"
              >
                  <template #date="row">
                      {{ toLocalTime(row.value.date) }}
                  </template>
              </vue3-datatable>
          </div>
      </div>

      <base-modal v-if="selectedDelivery" :title="$t('contract_reports.unload_records.title')" hide-footer @close="selectedDelivery = null" size="xl">
          <contract-report-unloads :delivery-note="selectedDelivery" class="mb-2" />
      </base-modal>

      <hr class="report-divider nopads">
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import LineUnloadRecordTable from "@/components/contractreport/LineUnloadRecordTable";
import PointUnloadRecordTable from "@/components/contractreport/PointUnloadRecordTable";
import {restApi} from "@/components/mixins/RestApiMixin";
import {downloadHelper} from "@/components/mixins/DownloadMixin";
import {inject} from "vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import BaseModal from "@/components/BaseModal";
import ContractReportUnloads from "@/components/contractreport/ContractReportUnloads";

export default {
    name: "ContractReportDeliveryNotes",
    components: {
        ContractReportUnloads,
        BaseModal,
        Vue3Datatable,
        LineUnloadRecordTable, PointUnloadRecordTable,
    },
    mixins: [timeUtils, restApi, downloadHelper],
    props: {
        deliveryNotes: {
            type: Array,
            default() {
                return []
            }
        },
        searchParams: {
            type: Object,
            default() {
                return {}
            }
        },
        reportsLoading: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            deliveriesVisible: false,
            totalAmount: 0,
            unloadedAmount: 0,
            totalArea: 0,
            totalDistance: 0,
            unit: '',
            tableItems: [],
            selectedDelivery: null,
            fields: [
                {field: "date", title: this.$t('delivery_note.date'), sort: true, headerClass: 'routa-table-header'},
                {field: "id", title: this.$t('common.id'), sort: true, headerClass: 'routa-table-header'},
                {field: "vehicle", title: this.$t('delivery_note.vehicle'), sort: true, headerClass: 'routa-table-header'},
                {field: "company", title: this.$t('daily_summary.company'), sort: true, headerClass: 'routa-table-header'},
                {field: "material", title: this.$t('delivery_note.material'), sort: true, headerClass: 'routa-table-header'},
                {field: "type_test_code", title: this.$t('material_station.material.type_test_code'), sort: true, headerClass: 'routa-table-header'},
                {field: "station", title: this.$t('material_station.title'), sort: true, headerClass: 'routa-table-header'},
                {field: "station_company", title: this.$t('material_station.station_owner'), sort: true, headerClass: 'routa-table-header'},
                {field: "amount", title: this.$t('delivery_note.amount'), sort: true, headerClass: 'routa-table-header'},
                {field: "unit", title: this.$t('delivery_note.unit'), sort: false, headerClass: 'routa-table-header'},
            ],
            material: null,
            loading: false
        }
    },
    computed: {
        averageDensity() {
            let avg = 0
            if (this.totalArea > 0) {
                let totalKg = this.unloadedAmount * 1000
                avg = totalKg / this.totalArea
            }
            return avg.toFixed(2)
        },
        materials() {
            let all = {"station": [], "storage": [], "manual": []}
            this.deliveryNotes.forEach(item => {
                if (item.station_material) {
                    all.station.push(item.station_material)
                }
                if (item.storage_material) {
                    all.storage.push(item.storage_material)
                }
                if (item.manually_added_material) {
                    all.storage.push(item.manually_added_material)
                }

            })
            return {
                "station": [...new Map(all.station.map(item => [item.id, item])).values()],
                "storage": [...new Map(all.storage.map(item => [item.id, item])).values()],
                "manual": [...new Map(all.manual.map(item => [item.id, item])).values()],
            }
        }
    },
    watch: {
        deliveryNotes: function () {
            this.setTableItems()
        },
        material: function () {
            this.setTableItems()
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    mounted() {
        this.setTableItems()
    },
    methods: {
        showDeliveries(item) {
            this.selectedDelivery = this.deliveryNotes.find(note => note.id === item.id)
        },
        getMaterialName(item) {
            return item.station_material ?
                item.station_material.material.name + " (" + (item.station_material.type_test_code) + ")" :
                item.storage_material ? item.storage_material.name :
                    item.manually_added_material ? item.manually_added_material.material : ''
        },
        getStationName(item) {
            return item.station_material ?
                item.station_material.station.name :
                item.storage_material ? item.storage_material.storage.name : ''
        },
        setTableItems: function () {
            this.totalAmount = 0
            this.unloadedAmount = 0
            this.totalArea = 0
            this.totalDistance = 0
            this.unit = ''
            this.tableItems = []
            if (this.deliveryNotes.length > 0) {
                this.loading = true;
                this.deliveryNotes.forEach(item => {
                        if (this.material === null || (item.station_material && item.station_material.id === this.material) || (item.storage_material && item.storage_material.id === this.material)) {
                            if (!this.unit) {
                                this.unit = item.unit
                            }
                            this.totalAmount += item.amount
                            this.tableItems.push({
                                date: item.created,
                                id: item.id,
                                vehicle: item.vehicle.license_plate,
                                company: item.vehicle.company.name,
                                material: this.getMaterialName(item),
                                type_test_code: item.station_material ? item.station_material.type_test_code : '',
                                station: this.getStationName(item),
                                station_company: item.station_material ? item.station_material.station.company.name : '',
                                amount: item.amount,
                                unit: item.unit,
                                line_unload_records: item.line_unload_records,
                                point_unload_records: item.point_unload_records,
                                _showDetails: true
                            })
                            this.increaseTotals(item.line_unload_records, item.point_unload_records)
                        }
                    }
                )
                this.loading = false
            }
        },
        increaseTotals(lineRecords, pointRecords) {
            if (lineRecords) {
                lineRecords.forEach(record => {
                    if (record.travel_distance && record.travel_distance > 0) {
                        this.totalDistance += record.travel_distance
                        this.totalArea += record.travel_distance * record.width
                        this.unloadedAmount += record.amount
                    }
                })
            }
            if (pointRecords) {
                pointRecords.forEach(record => {
                    if (record.area_size && record.area_size > 0) {
                        this.totalArea += record.area_size
                        this.unloadedAmount += record.amount
                    }
                })
            }
        },
        exportExcel: function () {
            this.loading = true
            this.restGetDownloadFile(this.materialDeliveryReportUrl, this.getCommonReportParams(this.searchParams), this.excelDownloadSuccess, this.fileDownloadFail)
        },
        excelDownloadSuccess: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('contract_reports.delivery_report_file_name'))
        },
        fileDownloadFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        }
    }
}
</script>
<style scoped>
:deep(.b-table-details > td:first-child) {
    padding-top: 0;
    padding-bottom: 0;
}

.map-link {
    padding: 0;
    margin: 0;
    color: #007BFF;
    font-size: .9em;
    cursor: pointer;
}

.map-link:hover {
    text-decoration: underline;
    font-weight: bold;
}

</style>
